import React, { useEffect } from 'react';
import { measureMobilePerformance, isMobileDevice } from '@/utils/performance';
import { logger } from '@/utils/logger';

interface MobilePerformanceMonitorProps {
  children: React.ReactNode;
  enableReporting?: boolean;
}

export const MobilePerformanceMonitor: React.FC<MobilePerformanceMonitorProps> = ({
  children,
  enableReporting = process.env.NODE_ENV === 'production'
}) => {
  useEffect(() => {
    if (!enableReporting || !isMobileDevice()) return;

    // Monitor performance metrics
    const monitorPerformance = () => {
      const metrics = measureMobilePerformance();
      if (metrics) {
        // Report slow performance on mobile
        if (metrics.domContentLoaded > 3000) {
          logger.warn('Slow DOM Content Loaded detected on mobile', { 
            component: 'MobilePerformanceMonitor',
            action: 'performanceCheck',
            metric: 'domContentLoaded'
          }, { duration: metrics.domContentLoaded });
        }
        
        if (metrics.firstByte > 1000) {
          logger.warn('Slow TTFB detected on mobile', { 
            component: 'MobilePerformanceMonitor',
            action: 'performanceCheck',
            metric: 'firstByte'
          }, { duration: metrics.firstByte });
        }

        // Log network quality issues
        if (metrics.networkType === 'slow-2g' || metrics.networkType === '2g') {
          logger.info('Slow network connection detected', { 
            component: 'MobilePerformanceMonitor',
            action: 'networkCheck'
          }, { networkType: metrics.networkType });
        }

        // Memory warnings for mobile
        if (metrics.memory && metrics.memory.used > 50) {
          logger.warn('High memory usage detected on mobile', { 
            component: 'MobilePerformanceMonitor',
            action: 'memoryCheck'
          }, { memoryUsed: metrics.memory.used });
        }
      }
    };

    // Wait for page load to complete
    if (document.readyState === 'complete') {
      setTimeout(monitorPerformance, 1000);
    } else {
      window.addEventListener('load', () => {
        setTimeout(monitorPerformance, 1000);
      });
    }

    // Monitor Core Web Vitals
    if ('PerformanceObserver' in window) {
      try {
        // Largest Contentful Paint
        const lcpObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            const lcp = entry.startTime;
            if (lcp > 2500) {
              logger.warn('Poor LCP detected on mobile', { 
                component: 'MobilePerformanceMonitor',
                action: 'webVitalCheck',
                metric: 'LCP'
              }, { lcp });
            }
          }
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

        // Cumulative Layout Shift
        const clsObserver = new PerformanceObserver((list) => {
          let clsValue = 0;
          for (const entry of list.getEntries()) {
            if (!(entry as any).hadRecentInput) {
              clsValue += (entry as any).value;
            }
          }
          if (clsValue > 0.1) {
            logger.warn('Poor CLS detected on mobile', { 
              component: 'MobilePerformanceMonitor',
              action: 'webVitalCheck',
              metric: 'CLS'
            }, { clsValue });
          }
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });

      } catch (e) {
        logger.warn('Performance monitoring setup failed', { 
          component: 'MobilePerformanceMonitor',
          action: 'setup' 
        }, e);
      }
    }
  }, [enableReporting]);

  return <>{children}</>;
};